/**
 * 통합 지도 컴포넌트
 * 조건에 따라 카카오맵 또는 OpenStreetMap(Leaflet)을 자동으로 선택
 * 
 * 조건:
 * 1. user.area === "00000" && user.grade === 2
 * 2. REACT_APP_BASE_URL === "https://mamolink.nongcall.com"
 * 
 * 두 조건 모두 만족 → 한국 영역: 카카오맵, 해외 영역: OSM
 * 조건 불만족 → 무조건 카카오맵
 */

import { ReactNode } from "react";
import { Map as KakaoMap } from "react-kakao-maps-sdk";
import { LeafletMapWrapper } from "./LeafletMapWrapper";
import { useMapProvider } from "../../hooks/useMapProvider";
import { kakaoToLeafletZoom, leafletToKakaoZoom } from "../../utils/mapUtils";
import { MapProviderProvider } from "./MapProviderContext";

export interface UnifiedMapProps {
  center: { lat: number; lng: number };
  level: number; // 카카오맵 줌 레벨 (1~14)
  style?: React.CSSProperties;
  children?: ReactNode;
  onIdle?: (target: any) => void;
  onClick?: () => void;
  onCreate?: (map: any) => void;
  mapTypeId?: number; // 1: 일반, 3: 위성
  disableDoubleClickZoom?: boolean;
  minLevel?: number; // 최소 줌 레벨 (카카오맵 호환)
}

/**
 * 통합 지도 컴포넌트
 * 카카오맵과 동일한 인터페이스 제공
 */
export const UnifiedMap: React.FC<UnifiedMapProps> = ({
  center,
  level,
  style,
  children,
  onIdle,
  onClick,
  onCreate,
  mapTypeId = 1,
  disableDoubleClickZoom = false,
  minLevel, // 카카오맵에만 적용 (Leaflet은 무시)
}) => {
  const { mapProvider } = useMapProvider(center.lat, center.lng, level);
  
  // Leaflet Map을 카카오맵 호환 객체로 변환하는 헬퍼 함수
  const createKakaoLikeMap = (map: any) => {
    if (!map || !map.getBounds) return null;
    try {
        const bounds = map.getBounds();
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();
        
        // 카카오맵 호환 Bounds 객체 생성
        const kakaoLikeBounds = {
        getSouthWest: () => ({
          getLat: () => sw.lat,
          getLng: () => sw.lng,
        }),
        getNorthEast: () => ({
          getLat: () => ne.lat,
          getLng: () => ne.lng,
        }),
        contain: (latlng: any) => {
          // 카카오맵 LatLng 객체 또는 Leaflet LatLng 처리
          let lat: number, lng: number;
          
          if (latlng && typeof latlng.getLat === 'function') {
            // 카카오맵 형식: {getLat(), getLng()}
            lat = latlng.getLat();
            lng = latlng.getLng();
          } else if (latlng && typeof latlng.lat !== 'undefined') {
            // Leaflet 형식: {lat, lng}
            lat = latlng.lat;
            lng = latlng.lng;
          } else {
            console.error("Invalid latlng format:", latlng);
            return false;
          }
          
          // Leaflet bounds.contains는 [lat, lng] 형식을 받음
          const result = bounds.contains([lat, lng]);
          
          
          return result;
        },
      };
      
      return {
        getBounds: () => kakaoLikeBounds,
        getLevel: () => leafletToKakaoZoom(map.getZoom()),
        getCenter: () => ({
          getLat: () => map.getCenter().lat,
          getLng: () => map.getCenter().lng,
        }),
        setLevel: (lvl: number) => {
          try {
            // 맵이 준비되었는지 확인
            if (!map || !map.getContainer) {
              return;
            }
            const container = map.getContainer();
            if (!container || !container.parentElement) {
              // 맵이 아직 DOM에 마운트되지 않았거나 제거된 경우
              return;
            }
            // 맵이 준비될 때까지 대기
            if (map.whenReady) {
              map.whenReady(() => {
                try {
                  if (map && map.getContainer && map.getContainer()) {
                    map.setZoom(kakaoToLeafletZoom(lvl));
                  }
                } catch (e) {
                  console.error('setLevel 에러:', e);
                }
              });
            } else {
              // whenReady가 없으면 직접 실행 (이미 준비된 상태)
              map.setZoom(kakaoToLeafletZoom(lvl));
            }
          } catch (e) {
            console.error('setLevel 에러:', e);
          }
        },
        setZoom: (zoom: number) => {
          try {
            // 맵이 준비되었는지 확인
            if (!map || !map.getContainer) {
              return;
            }
            const container = map.getContainer();
            if (!container || !container.parentElement) {
              // 맵이 아직 DOM에 마운트되지 않았거나 제거된 경우
              return;
            }
            // 맵이 준비될 때까지 대기
            if (map.whenReady) {
              map.whenReady(() => {
                try {
                  if (map && map.getContainer && map.getContainer()) {
                    map.setZoom(kakaoToLeafletZoom(zoom));
                  }
                } catch (e) {
                  console.error('setZoom 에러:', e);
                }
              });
            } else {
              // whenReady가 없으면 직접 실행 (이미 준비된 상태)
              map.setZoom(kakaoToLeafletZoom(zoom));
            }
          } catch (e) {
            console.error('setZoom 에러:', e);
          }
        },
        // 원본 Leaflet map 접근 (필요시)
        _leafletMap: map,
      };
    } catch (e) {
      console.error("createKakaoLikeMap error:", e);
      return null;
    }
  };
  
  const leafletZoom = kakaoToLeafletZoom(level);
  
  // center가 유효한지 확인
  if (!center || typeof center.lat !== 'number' || typeof center.lng !== 'number') {
    console.error('🗺️ [UnifiedMap] Invalid center:', center);
    return <div>지도를 불러오는 중...</div>;
  }
  
  console.log(`🗺️ [UnifiedMap] mapProvider=${mapProvider}, center=${center.lat.toFixed(4)},${center.lng.toFixed(4)}, level=${level}`);

  if (mapProvider === "osm") {
    return (
      <MapProviderProvider mapProvider="osm">
        <LeafletMapWrapper
          center={center}
          zoom={leafletZoom}
          style={style}
          onIdle={(map) => {
            if (onIdle) {
              const kakaoLikeMap = createKakaoLikeMap(map);
              if (kakaoLikeMap) onIdle(kakaoLikeMap);
            }
          }}
          onClick={onClick}
          onCreate={(map) => {
            if (onCreate) {
              const kakaoLikeMap = createKakaoLikeMap(map);
              if (kakaoLikeMap) onCreate(kakaoLikeMap);
            }
          }}
          mapTypeId={mapTypeId}
        >
          {children}
        </LeafletMapWrapper>
      </MapProviderProvider>
    );
  }

  return (
    <MapProviderProvider mapProvider="kakao">
      <KakaoMap
        center={center}
        level={level}
        style={style}
        onIdle={onIdle}
        onClick={onClick}
        onCreate={onCreate}
        mapTypeId={mapTypeId}
        disableDoubleClickZoom={disableDoubleClickZoom}
        minLevel={minLevel}
      >
        {children}
      </KakaoMap>
    </MapProviderProvider>
  );
};

